import { useInfiniteQuery } from "@tanstack/react-query";
import { templateService } from "../api/services/templateService";
import type {
  Template,
  TemplateFilters,
  TemplateResult,
} from "../schemas/templateSchema";
import { useTemplates } from "./useTemplates";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type TemplatesInfiniteFilters = Omit<
  TemplateFilters,
  "page" | "pageSize"
> & {
  pageSize?: number;
};

export const useTemplatesInfinite = (
  filters: TemplatesInfiniteFilters = {},
) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;
  const mutations = useTemplates(undefined, { enabled: false });

  const query = useInfiniteQuery<TemplateResult, Error>({
    queryKey: ["templates", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      templateService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const templates: Template[] =
    query.data?.pages.flatMap((p) => p.templates) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    ...mutations,
    templates,
    total,
    isLoading: query.isLoading || mutations.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
